import { clsx } from 'clsx'
import { CaseSensitive, ChevronDown, ChevronUp, Loader2, Search, WholeWord, X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useSearch } from '@/store/searchStore'
import { useUi } from '@/store/uiStore'
import { IconButton } from '../ui/Button'

export function SearchBar() {
  const open = useUi((s) => s.searchOpen)
  const s = useSearch()
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) setTimeout(() => input.current?.select(), 0)
    else s.clear()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    const t = setTimeout(() => useSearch.getState().run(), 250)
    return () => clearTimeout(t)
  }, [s.query])

  if (!open) return null
  const close = () => useUi.getState().setSearchOpen(false)
  return (
    <div className="absolute top-3 right-3 left-3 z-30 ml-auto flex max-w-md animate-pop items-center gap-1 rounded-2xl border border-line bg-surface p-1.5 shadow-lg sm:left-auto sm:w-[420px]">
      <div className="relative flex flex-1 items-center">
        {s.searching ? (
          <Loader2 className="absolute left-2.5 size-4 animate-spin text-fg-3" />
        ) : (
          <Search className="absolute left-2.5 size-4 text-fg-3" />
        )}
        <input
          ref={input}
          autoFocus
          type="search"
          aria-label="Find in document"
          placeholder="Find in document"
          value={s.query}
          onChange={(e) => s.setQuery(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation()
            if (e.key === 'Enter') {
              e.preventDefault()
              s.next(e.shiftKey ? -1 : 1)
            }
            if (e.key === 'Escape') close()
          }}
          className="h-9 w-full rounded-xl bg-surface-2 pr-20 pl-8 text-sm outline-none placeholder:text-fg-3 focus:ring-2 focus:ring-accent/40 [&::-webkit-search-cancel-button]:hidden"
        />
        <span className="pointer-events-none absolute right-2.5 text-fg-3 text-xs tabular-nums">
          {s.query.trim()
            ? s.matches.length
              ? `${s.active + 1} / ${s.matches.length}`
              : s.searching
                ? ''
                : 'No results'
            : ''}
        </span>
      </div>
      <IconButton
        label="Match case"
        size="sm"
        active={s.caseSensitive}
        onClick={() => s.setOptions({ caseSensitive: !s.caseSensitive })}
      >
        <CaseSensitive className="size-[18px]" />
      </IconButton>
      <IconButton
        label="Whole words"
        size="sm"
        active={s.wholeWord}
        onClick={() => s.setOptions({ wholeWord: !s.wholeWord })}
      >
        <WholeWord className="size-[18px]" />
      </IconButton>
      <IconButton label="Previous match" size="sm" disabled={!s.matches.length} onClick={() => s.next(-1)}>
        <ChevronUp className="size-4" />
      </IconButton>
      <IconButton label="Next match" size="sm" disabled={!s.matches.length} onClick={() => s.next(1)}>
        <ChevronDown className="size-4" />
      </IconButton>
      <IconButton label="Close search" size="sm" className={clsx()} onClick={close}>
        <X className="size-4" />
      </IconButton>
    </div>
  )
}
